import React, { useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Footer from '../Footer';
import { BuiltLogoMark } from './BuiltLogoMark';

export type OpsNavItem = {
  name: string;
  href: string;
  /** Match only exact path (default: prefix match except for homeHref) */
  exact?: boolean;
  /** Custom active matcher when route nesting is non-trivial */
  isActive?: (pathname: string) => boolean;
  description?: string;
};

export type OpsNavGroup = {
  id: string;
  label: string;
  items: OpsNavItem[];
};

interface OpsShellLayoutProps {
  title: string;
  roleLabel: string;
  navAriaLabel: string;
  homeHref: string;
  groups: OpsNavGroup[];
  /** Optional short tip shown under the shell title on desktop */
  tip?: string;
}

function isItemActive(pathname: string, item: OpsNavItem, homeHref: string): boolean {
  if (item.isActive) return item.isActive(pathname);
  if (item.exact || item.href === homeHref) {
    return pathname === item.href;
  }
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
}

/**
 * Shared shell for Organizer / Judge workspaces (mirrors AdminLayout IA).
 * Keeps Bilt dark + orange tokens; focuses on clear nav, skip link, and mobile drawer.
 */
export const OpsShellLayout: React.FC<OpsShellLayoutProps> = ({
  title,
  roleLabel,
  navAriaLabel,
  homeHref,
  groups,
  tip,
}) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const activeLabel = useMemo(() => {
    for (const group of groups) {
      for (const item of group.items) {
        if (isItemActive(location.pathname, item, homeHref)) return item.name;
      }
    }
    return title;
  }, [groups, homeHref, location.pathname, title]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const nav = (
    <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto" aria-label={navAriaLabel}>
      {groups.map((group) => {
        const isCollapsed = !!collapsed[group.id];
        return (
          <div key={group.id}>
            <button
              type="button"
              className="flex w-full items-center justify-between px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 cursor-pointer"
              onClick={() => setCollapsed((c) => ({ ...c, [group.id]: !c[group.id] }))}
              aria-expanded={!isCollapsed}
            >
              <span>{group.label}</span>
              <span aria-hidden="true" className="text-gray-600">
                {isCollapsed ? '+' : '−'}
              </span>
            </button>
            {!isCollapsed && (
              <ul className="mt-1 space-y-0.5">
                {group.items.map((item) => {
                  const active = isItemActive(location.pathname, item, homeHref);
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex flex-col px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 min-h-[40px] cursor-pointer ${
                          active
                            ? 'bg-orange-500/[0.1] text-orange-300 border border-orange-500/30'
                            : 'text-gray-300 border border-transparent hover:bg-white/[0.04] hover:text-white'
                        }`}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="truncate">{item.name}</span>
                        {item.description ? (
                          <span className="text-[11px] font-normal text-gray-500 mt-0.5 leading-snug">
                            {item.description}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen overflow-x-hidden flex flex-col bg-[#0a0a0a]">
      <a
        href="#ops-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:rounded-lg focus:bg-orange-500 focus:px-3 focus:py-2 focus:text-sm focus:text-black"
      >
        Skip to main content
      </a>

      <div className="lg:hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06] bg-[#0c0c0c]">
          <div className="flex items-center min-w-0 flex-1 gap-3">
            <BuiltLogoMark size="sm" />
            <div className="min-w-0">
              <h1 className="font-display text-lg font-semibold tracking-tight text-white truncate">{title}</h1>
              <p className="text-[11px] text-gray-500 truncate">{activeLabel}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-gray-400 hover:text-white p-2 flex-shrink-0 min-h-[44px] min-w-[44px] cursor-pointer"
            aria-expanded={isMobileMenuOpen}
            aria-controls="ops-mobile-nav"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex flex-1">
        <aside
          id="ops-mobile-nav"
          className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:block fixed lg:static inset-y-0 left-0 z-50 w-64 border-r border-white/[0.06] lg:min-h-screen bg-[#0c0c0c]`}
        >
          <div className="flex flex-col h-full">
            <div className="hidden lg:flex items-center justify-between px-4 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-3 min-w-0">
                <BuiltLogoMark size="sm" />
                <div className="min-w-0">
                  <h1 className="font-display text-lg font-semibold tracking-tight text-white">{title}</h1>
                  {tip ? <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{tip}</p> : null}
                </div>
              </div>
            </div>

            {nav}

            <div className="border-t border-white/[0.06] p-4">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-brand-tile rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-medium">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{user?.username}</p>
                  <p className="text-xs text-gray-500">{roleLabel}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:bg-white/[0.04] hover:text-white rounded-lg transition-colors min-h-[40px] cursor-pointer"
                >
                  Back to Bilt
                </Link>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-300 hover:bg-white/[0.04] hover:text-white rounded-lg transition-colors min-h-[40px] cursor-pointer"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </aside>

        {isMobileMenuOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-black/50 z-40 lg:hidden cursor-pointer"
            aria-label="Close menu overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        <div className="flex-1 lg:ml-0 min-w-0 overflow-x-hidden">
          <main id="ops-main" className="p-4 sm:p-6 max-w-[1400px] text-white">
            <Outlet />
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OpsShellLayout;
